import React, { useEffect, useState, useRef, useMemo } from "react";
import { Button, Tabs, message, Form, Input, Upload, Select, Modal, Divider, Progress } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import type { UploadChangeParam } from "antd/es/upload";
import type { FormInstance, TabsProps } from "antd";
import {
  LoadingOutlined,
  PlusSquareFilled,
  CloudUploadOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useParams, useOutletContext } from "react-router-dom";
import { cloneDeep, delay, debounce } from "lodash-es";
import { useSelector } from "react-redux";
import StickyBox from "react-sticky-box";
import api from "@/api";
import apiStaging from "@/api/staging";
import { format, findDeep, changeSize, getAiModelDetail } from "@/utils";
import { appNameReg } from "@/utils/constant";
import { uploadBaseUrl } from "@/api/release";
import ApplyDown from "./ApplyDown";

import dataJson from "@/../public/data.json";

const prefix = "myAiModelDetail-versionManage";
import "./index.less";
import { getAuthTokenValue } from "@kaihong/utils/lib/khAuthToken";
import { useTypeKeyInfo } from "@/hooks/useTypeKeyInfo";

const VersionManage: React.FC = () => {
  const { typeKey, id } = useParams();
  const { isTampered = false } = useOutletContext<{ isTampered?: boolean }>() || {};
  const {
    release,
    staging,
    please_enter,
    icon_text,
    one_bars,
    two_bars,
    please_upload,
    save_text,
    save_success_text,
    req_err,
    cancelText,
    confirmText,
    my_ai_model_detail,
    asterisk,
    name_label,
    description_label,
    my_ai_model,
  } = dataJson;
  const [activeKey, setActiveKey] = useState("1");
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [iconLoading, setIconLoading] = useState(false);
  const [iconImageUrl, setIconImageUrl] = useState<string>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const detailData = useRef<any>();

  const [packageData, setPackageData] = useState<any>();
  const [packageLoading, setPackageLoading] = useState(false);
  const [isBaseFinish, setIsBaseFinish] = useState(false);
  const [isVersionFinish, setIsVersionFinish] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [applyDownVisible, setApplyDownVisible] = useState(false);
  const [kaihongOSVersionOption, setKaihongOSVersionOption] = useState<any[]>([]);
  const [supportChipOption, setSupportChipOption] = useState<any[]>([]);
  const [industryOption, setIndustryOption] = useState<any[]>([]);
  const [usageGuide, setUsageGuide] = useState<any>({});
  const [usageGuideLoading, setUsageGuideLoading] = useState(false);
  const [uploadProgressPdf, setUploadProgressPdf] = useState(0);
  const [savaLoading, setSavaLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cancelReleaseLoading, setCancelReleaseLoading] = useState(false);
  const [cancelDownLoading, setCancelDownLoading] = useState(false);

  const aiModelDetail = useSelector((state: any) => state.aiModelDetail);
  const appTypes = useSelector((state: any) => state.appTypes);
  const { typeKeyInfo } = useTypeKeyInfo(typeKey, appTypes);
  const packageType = useRef<string>("");

  const [baseForm] = Form.useForm();
  const baseFormValues = Form.useWatch([], baseForm);

  const [versionForm] = Form.useForm();
  const versionFormValues = Form.useWatch([], versionForm);

  const uploadUrl = `${uploadBaseUrl}/app/upload/public/${id}`;

  const onChange = (key: string) => {
    setActiveKey(key);
  };

  const renderBtn = (item: string) => {
    const newShowMode =
      detailData.current?.histories?.length > 1 && detailData.current?.histories[0]?.showMode === "AUDIT_UPGRADE";
    switch (item) {
      case "save":
        return (
          <Button
            type="primary"
            loading={savaLoading}
            ghost
            style={{ marginLeft: "16px" }}
            disabled={isTampered}
            onClick={handleSave}
          >
            {save_text}
          </Button>
        );
      case "submitReview":
        return (
          <Button
            type="primary"
            loading={submitLoading}
            style={{ marginLeft: "16px" }}
            disabled={isTampered}
            onClick={submitReview}
          >
            {release.submit_review}
          </Button>
        );
      case "cancelRelease":
        return (
          <Button type="primary" style={{ marginLeft: "16px" }} disabled={isTampered} onClick={cancelRelease}>
            {release.cancel_release}
          </Button>
        );
      case "down":
        if (newShowMode) {
          break;
        }
        return (
          <Button
            type="primary"
            ghost
            style={{ marginLeft: "16px" }}
            disabled={isTampered}
            onClick={() => {
              setApplyDownVisible(true);
            }}
          >
            {release.audit_down}
          </Button>
        );
      case "cancelDown":
        return (
          <Button type="primary" style={{ marginLeft: "16px" }} disabled={isTampered} onClick={cancelDown}>
            {release.cancel_down}
          </Button>
        );
      default:
        return;
    }
  };

  const renderTabBar: TabsProps["renderTabBar"] = (props, DefaultTabBar) => (
    <StickyBox offsetTop={0} offsetBottom={20} style={{ zIndex: 99, background: "#fff", paddingTop: 24 }}>
      <DefaultTabBar {...props} />
    </StickyBox>
  );

  const scrollErrDom = (domClass: string, delayTime?: number) => {
    if (delayTime) {
      delay(() => {
        const errorList = (document as any).querySelectorAll(domClass);
        //由于校验失败ant会自动给失败表单项添加类名，直接获取即可
        if (errorList?.length) {
          errorList[0]?.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      }, delayTime);
    } else {
      const errorList = (document as any).querySelectorAll(domClass);
      //由于校验失败ant会自动给失败表单项添加类名，直接获取即可
      if (errorList?.length) {
        errorList[0]?.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
  };

  // 校验是否有图片正在上传
  const checkIsImgUploading = () => {
    const previewDataCopy = cloneDeep(previewData);
    const previewChangeLoading = previewDataCopy?.some((item: any) => {
      return item.loading;
    });
    return iconLoading || previewLoading || previewChangeLoading;
  };

  // 校验当前表格字段填写
  const validateFieldKF = async (value: { form: FormInstance; name: string }) => {
    try {
      await value.form.validateFields([value.name]);
      return false;
    } catch (err) {
      return true;
    }
  };

  // 重置表单校验
  const resetValidate = () => {
    baseForm.resetFields();
    baseForm.setFieldsValue(baseFormValues); // 重置表单值，可选
    versionForm.resetFields();
    versionForm.setFieldsValue(versionFormValues); // 重置表单值，可选
  };

  // 处理请求参数
  const handleReq = async (isValidate: boolean) => {
    if (checkIsImgUploading()) {
      message.warning(release.upload_img_ing);
      return;
    } else if (usageGuideLoading) {
      message.warning(release.upload_guide_ing);
      return;
    } else if (packageLoading) {
      message.warning(release.upload_package_ing);
      return;
    }

    let baseValues: any;
    let versionValues: any;

    if (isValidate) {
      if (activeKey === "1") {
        try {
          baseValues = await baseForm.validateFields();
        } catch (error) {
          scrollErrDom(".ant-form-item-has-error", 100);
          return;
        }

        if (baseValues) {
          try {
            versionValues = await versionForm.validateFields();
          } catch (error) {
            setActiveKey("2");
            scrollErrDom(".ant-form-item-has-error", 300);
            return;
          }
        }
      } else {
        try {
          baseValues = await baseForm.validateFields();
        } catch (error) {
          setActiveKey("1");
          scrollErrDom(".ant-form-item-has-error", 300);
          return;
        }
        if (baseValues) {
          try {
            versionValues = await versionForm.validateFields();
          } catch (error) {
            scrollErrDom(".ant-form-item-has-error", 100);
            return;
          }
        }
      }
    } else {
      if (
        baseFormValues.name == undefined ||
        baseFormValues.name == null ||
        baseFormValues.name == "" ||
        (!(baseFormValues.name == undefined || baseFormValues.name == null || baseFormValues.name == "") &&
          (await validateFieldKF({ form: baseForm, name: "name" })))
      ) {
        setActiveKey("1");
        resetValidate();
        baseForm.validateFields(["name"]);
        scrollErrDom(".ant-form-item-has-error", 100);
        return;
      }
      // 校验版本号存在 但不符合填写规范不能保存
      if (
        !(
          versionFormValues.version == undefined ||
          versionFormValues.version == null ||
          versionFormValues.version == ""
        ) &&
        (await validateFieldKF({ form: versionForm, name: "version" }))
      ) {
        setActiveKey("2");
        resetValidate();
        versionForm.validateFields(["version"]);
        scrollErrDom(".ant-form-item-has-error", 100);
        return;
      }
      // 隐私声明存在 但不符合填写规范不能保存
      if (
        !(
          versionFormValues.privacy == undefined ||
          versionFormValues.privacy == null ||
          versionFormValues.privacy == ""
        ) &&
        (await validateFieldKF({ form: versionForm, name: "privacy" }))
      ) {
        setActiveKey("2");
        resetValidate();
        versionForm.validateFields(["privacy"]);
        scrollErrDom(".ant-form-item-has-error", 250);
        return;
      }
      baseValues = await baseForm.getFieldsValue();
      versionValues = await versionForm.getFieldsValue();
    }

    try {
      const resourcesArr = [];
      const iconIds = baseValues?.iconResources;
      !!iconIds &&
        resourcesArr.push({
          resourceIds: [iconIds],
          type: "ICON",
        });

      const previewArr = [];
      const previewFormData = baseValues?.previewFormData;
      if (previewFormData?.length) {
        const pictureIds = previewFormData.map((item: any) => item.id);

        previewArr.push({
          resourceIds: pictureIds,
          type: "PREVIEW_PICTURE",
        });
      }

      const usageGuideArr = [];
      const usageGuideIds = versionValues?.usageGuideResources?.id;
      !!usageGuideIds &&
        usageGuideArr.push({
          resourceIds: [usageGuideIds],
          type: "USER_GUIDE",
        });

      const kaihongOSVersionId = versionValues?.kaihongOSVersionId;
      const supportOS =
        kaihongOSVersionId == null || kaihongOSVersionId === ""
          ? undefined
          : kaihongOSVersionId === "NO_RELATE"
          ? {}
          : {
              id: kaihongOSVersionId,
              supportType: versionValues?.kaihongOSVersionType,
            };
      const req = {
        description: baseValues?.description,
        id: id,
        name: baseValues?.name,
        introduction: baseValues?.introduction,
        instanceIds: versionValues?.instanceIds?.instanceId ? [versionValues?.instanceIds?.instanceId] : [],
        privacy: versionValues?.privacy,
        resources: [...resourcesArr, ...usageGuideArr, ...previewArr],
        supportOS,
        supportChip: baseValues?.supportChip,
        version: versionValues?.version,
        industry: baseValues?.industry,
      };
      return req;
    } catch (errorInfo: any) {
      console.error(errorInfo);
      return;
    }
  };

  const handleSave = async () => {
    setSavaLoading(true);
    const req = await handleReq(false);
    if (req) {
      try {
        const res: any = await api.saveAiModel(req);
        setSavaLoading(false);
        if (res?.code == 200) {
          getAiModelDetail(id);
          message.success(save_success_text);
        } else {
          message.error(res.msg);
        }
      } catch (errorInfo: any) {
        setSavaLoading(false);
        message.error(errorInfo?.response?.data?.msg || req_err);
      }
    }
    setSavaLoading(false);
  };

  // 提交审核
  const submitReview = debounce(async () => {
    const req = await handleReq(true);
    if (req) {
      try {
        setSubmitLoading(true);
        const res: any = await api.saveNextAiModel(req);
        if (res?.code == 200) {
          // 接口要区分是否是升级的提交
          let res2: any = null;
          if (detailData.current?.isUpgrade) {
            res2 = await api.auditUpgradeAiModel({ id });
          } else {
            res2 = await api.auditReleaseAiModel({ id });
          }
          setSubmitLoading(false);
          if (res2?.code == 200) {
            message.success(release.wait_review);
            getAiModelDetail(id);
          } else {
            message.error(res2.msg);
          }
        } else {
          message.error(res.msg);
        }
      } catch (errorInfo: any) {
        setSubmitLoading(false);
        message.error(errorInfo?.response?.data?.msg || req_err);
      }
    }
  }, 300);

  const cancelRelease = async () => {
    const modal = Modal.confirm({
      icon: null,
      centered: true,
      closable: true,
      width: 350,
      title: release.cancel_release_title,
      content: release.cancel_release_content,
      footer: (
        <div style={{ textAlign: "right", marginTop: "20px" }}>
          <Button
            type="primary"
            ghost
            onClick={() => {
              modal.destroy();
            }}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            style={{ marginLeft: "24px" }}
            loading={cancelReleaseLoading}
            onClick={async () => {
              try {
                setCancelReleaseLoading(true);
                let res2: any = null;
                if (detailData.current?.isUpgrade) {
                  res2 = await api.cancelUpgradeAiModel({ id });
                } else {
                  res2 = await api.cancelReleaseAiModel({ id });
                }
                setCancelReleaseLoading(false);
                if (res2?.code == 200) {
                  message.success(release.cancel_release_success);
                  getAiModelDetail(id);
                  modal.destroy();
                } else {
                  message.error(res2.msg);
                }
              } catch (errorInfo: any) {
                setCancelReleaseLoading(false);
                message.error(errorInfo?.response?.data?.msg || req_err);
              }
            }}
          >
            {confirmText}
          </Button>
        </div>
      ),
    });
  };

  const cancelDown = async () => {
    const modal = Modal.confirm({
      icon: null,
      centered: true,
      closable: true,
      width: 350,
      title: release.cancel_down,
      content: release.cancel_down_content,
      footer: (
        <div style={{ textAlign: "right", marginTop: "20px" }}>
          <Button
            type="primary"
            ghost
            onClick={() => {
              modal.destroy();
            }}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            loading={cancelDownLoading}
            style={{ marginLeft: "24px" }}
            onClick={async () => {
              try {
                setCancelDownLoading(true);
                const res: any = await api.cancelDownAiModel({ id: id });
                setCancelDownLoading(false);
                if (res?.code == 200) {
                  message.success(release.cancel_down_success);
                  getAiModelDetail(id);
                  modal.destroy();
                } else {
                  message.error(res.msg);
                }
              } catch (errorInfo: any) {
                setCancelDownLoading(false);
                message.error(errorInfo?.response?.data?.msg || req_err);
              }
            }}
          >
            {confirmText}
          </Button>
        </div>
      ),
    });
  };

  const applyDownClose = () => {
    setApplyDownVisible(false);
    getAiModelDetail(id);
  };

  // 初始化数据
  const initHandle = async () => {
    const dataCopy = cloneDeep(detailData.current);

    let packObj = null;
    packObj = dataCopy?.appInstances?.length
      ? {
          ...dataCopy?.appInstances[0]?.packageResource,
          version: dataCopy?.appInstances[0]?.version,
          instanceId: dataCopy?.appInstances[0]?.instanceId,
        }
      : null;

    const iconItem = dataCopy?.resources?.find((item: any) => item.type == "ICON");
    const initPreviewData = dataCopy?.resources?.filter((item: any) => item.type == "PREVIEW_PICTURE");
    const initUsageGuide = dataCopy?.resources?.filter((item: any) => item.type == "USER_GUIDE");

    baseForm.setFieldsValue({
      name: dataCopy?.name || "",
      introduction: dataCopy?.introduction || "",
      description: dataCopy?.description || "",
      iconResources: iconItem?.id || "",
      previewFormData: initPreviewData,
      supportChip: dataCopy?.supportChip,
      industry: dataCopy?.industry,
    });
    versionForm.setFieldsValue({
      instanceIds: packObj,
      privacy: dataCopy?.privacy,
      usageGuideResources: initUsageGuide,
      kaihongOSVersionId: dataCopy?.support?.osCompatibility
        ? dataCopy?.support?.osCompatibility.id
          ? dataCopy?.support?.osCompatibility.id
          : "NO_RELATE"
        : undefined,
      kaihongOSVersionType: dataCopy?.support?.osCompatibility?.supportType,
      version: dataCopy?.version,
    });

    setPackageData(packObj);
    setIconImageUrl(iconItem?.url);
    setPreviewData(initPreviewData);
    setUsageGuide(initUsageGuide[0]);
  };

  // 图片上传前处理
  const beforeUpload: any = (file: RcFile, { width, height, size, accept = ["png"] }: any) => {
    return new Promise<void>((resolve, reject) => {
      const isAccept = accept.some((item: string) => file.type === `image/${item}`);
      const acceptText = accept.join("、").toUpperCase();

      if (!isAccept) {
        message.error(
          `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
        );

        reject();
        return;
      }
      if (size) {
        const imgSize = file.size;
        if (imgSize > size) {
          const sizeText = changeSize(size);
          message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);

          reject();
          return;
        }
      }
      const _URL = window.URL || window.webkitURL;
      const img = new Image();
      img.src = _URL.createObjectURL(file);

      img.onerror = () => {
        message.error(release.upload_fail);
      };
      img.onload = () => {
        if (img.width != width || img.height != height) {
          message.error(
            `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
          );

          reject();
        } else {
          resolve();
        }
      };
    });
  };

  // pdf上传前处理
  const pdfBeforeUpload: any = (file: RcFile, { size, accept = ["pdf"] }: any) => {
    return new Promise<void>((resolve, reject) => {
      const isAccept = accept.some((item: string) => file.type === `application/${item}`);
      const acceptText = accept.join("、").toUpperCase();
      if (!isAccept) {
        message.error(`${release.upload_accept_err.text1}${acceptText}`);

        reject();
        return;
      }
      if (size) {
        const imgSize = file.size;
        if (imgSize > size) {
          const sizeText = changeSize(size);
          message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);

          reject();
          return;
        }
      }
      resolve();
    });
  };
  // 上传pdf
  const pdfReq = async (options: any) => {
    setUsageGuideLoading(true);
    const { file } = options;

    setUsageGuide({ ...usageGuide, name: file.name });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("appId", id!);
    formData.append("type", "USER_GUIDE");
    formData.append("subType", "USER_GUIDE");
    try {
      api
        .uploadFile(formData, id, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          // 上传进度回调
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent?.progress) {
              const progressNumber = Math.floor(progressEvent?.progress * 100);
              setUploadProgressPdf(progressNumber);
            }
          },
          errorIntercept: true,
        })
        .then((res: any) => {
          if (res.code !== 200) {
            message.error(res.msg);
            versionForm.setFieldValue("usageGuideResources", null);
          } else {
            setUsageGuide(res.data);
            versionForm.setFieldValue("usageGuideResources", res.data);
          }
        })
        .catch((err: any) => {
          message.error(err?.response?.data?.msg || release.upload_fail);
        })
        .finally(() => {
          setUsageGuideLoading(false);
          versionForm.validateFields(["usageGuideResources"]);
          setUploadProgressPdf(0);
        });
    } catch (err) {
      console.error(err);
      setUsageGuideLoading(false);
    }
  };
  // 删除pdf
  const pdfDel = () => {
    setUsageGuide(null);
    versionForm.setFieldValue("usageGuideResources", null);
    versionForm.validateFields(["usageGuideResources"]);
  };

  // 图标新增
  const iconImageChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    const iconImageUrlCopy = cloneDeep(iconImageUrl);
    if (info.file.status === "uploading") {
      setIconLoading(true);
      setIconImageUrl(iconImageUrlCopy);
      return;
    } else {
      setIconLoading(false);
      setIconImageUrl(iconImageUrlCopy);
    }
    if (info.file.status === "done") {
      if (info.file.response.code === 200) {
        const iconUrl = info?.file?.response?.data?.url;
        const iconId = info?.file?.response?.data?.id;

        setIconLoading(false);
        setIconImageUrl(iconUrl);
        baseForm.setFieldValue("iconResources", iconId);
      } else {
        message.error(info.file.response.msg);
      }
    } else {
      if (!iconImageUrlCopy?.length) {
        baseForm.setFieldValue("iconResources", "");
        setIconImageUrl(iconImageUrlCopy);
      }
    }
    baseForm.validateFields(["iconResources"]);
  };

  // 预览图更换
  const previewImgUpdate = (info: UploadChangeParam<UploadFile>, index: number) => {
    const previewDataCopy = cloneDeep(previewData);
    if (info.file.status === "uploading") {
      previewDataCopy[index].loading = true;
      setPreviewData(previewDataCopy);
      return;
    } else {
      previewDataCopy[index].loading = false;
      setPreviewData(previewDataCopy);
    }
    if (info.file.status === "done") {
      if (info.file.response.code === 200) {
        previewDataCopy[index].id = info?.file?.response?.data?.id;
        previewDataCopy[index].url = info?.file?.response?.data?.url;
        previewDataCopy[index].loading = false;
        setPreviewData(previewDataCopy);
      } else {
        message.error(info.file.response.msg);
      }
    } else {
      setPreviewData(previewDataCopy);
    }
    baseForm.validateFields(["previewFormData"]);
  };

  // 预览图删除
  const previewImgDelete = (index: number) => {
    const previewDataCopy = cloneDeep(previewData);
    previewDataCopy.splice(index, 1);
    setPreviewData(previewDataCopy);
    baseForm.validateFields(["previewFormData"]);
  };

  // 预览图新增
  const previewImgChange = (info: UploadChangeParam<UploadFile>) => {
    const previewDataCopy = cloneDeep(previewData);
    if (info.file.status === "uploading") {
      setPreviewLoading(true);
      setPreviewData(previewDataCopy);
      return;
    } else {
      setPreviewLoading(false);
      setPreviewData(previewDataCopy);
    }
    if (info.file.status === "done") {
      if (info.file.response.code === 200) {
        previewDataCopy.push({
          id: info?.file?.response?.data?.id,
          url: info?.file?.response?.data?.url,
          loading: false,
        });
        setPreviewLoading(false);
        setPreviewData(previewDataCopy);
      } else {
        message.error(info.file.response.msg);
      }
    } else {
      setPreviewData(previewDataCopy);
    }
  };

  function isValidFilename(filename: string) {
    const regex = /\.tar(\.gz)?$/;
    return regex.test(filename);
  }
  // 软件包上传前处理
  const packageBeforeUpload = (file: RcFile) => {
    return new Promise<void>((resolve, reject) => {
      const isSizeOk = file.size <= my_ai_model_detail.packageMaxSize;
      if (typeKey === "AI_MODEL") {
        const isFileTypeOk = isValidFilename(file.name);
        if (!isFileTypeOk) {
          message.error(my_ai_model_detail.packageTypeErr);
          reject();
          return;
        }
      } else {
        const isFileTypeOk = detailData.current.subType.some((item: string) => {
          const regex = new RegExp(`${item}$`, "i");
          return regex.test(file.name);
        });
        if (!isFileTypeOk) {
          const errorTip =
            my_ai_model_detail.packageTypeErrPrefix +
            detailData.current.subType.join("/") +
            my_ai_model_detail.packageTypeErrSuffix;
          message.error(errorTip);
          reject();
          return;
        }
      }
      if (!isSizeOk) {
        message.error(my_ai_model_detail.packageSizeErr);
        reject();
        return;
      }
      // 类型和大小都对了就结合包类型给type赋值
      const nameSplitArr = file.name.split(".");
      const typeName = nameSplitArr[nameSplitArr.length - 1];
      const typeReq = typeName.toUpperCase();

      switch (typeKey) {
        case "AI_MODEL":
          packageType.current = "AI_MODEL";
          break;
        default:
          packageType.current = typeReq;
      }

      resolve();
    });
  };
  // 上传软件包
  const packageReq = async (options: any) => {
    setPackageLoading(true);
    const { file } = options;

    setPackageData({ ...packageData, name: file.name });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("appId", id!);
    formData.append("type", packageType.current);
    formData.append("subType", packageType.current);
    try {
      api
        .uploadFile(formData, id, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          // 上传进度回调
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent?.progress) {
              const progressNumber = Math.floor(progressEvent?.progress * 100);
              setUploadProgress(progressNumber);
            }
          },
          errorIntercept: true,
        })
        .then((res: any) => {
          if (res.code !== 200) {
            message.error(res.msg);
            versionForm.setFieldValue("instanceIds", null);
          } else {
            setPackageData(res.data);
            versionForm.setFieldValue("instanceIds", res.data);
          }
        })
        .catch((err: any) => {
          message.error(err?.response?.data?.msg || release.upload_fail);
        })
        .finally(() => {
          setPackageLoading(false);
          versionForm.validateFields(["instanceIds"]);
          setUploadProgress(0);
        });
    } catch (err) {
      console.error(err);
      setPackageLoading(false);
    }
  };
  // 删除软件包
  const packageDel = () => {
    setPackageData(null);
    versionForm.setFieldValue("instanceIds", null);
    versionForm.validateFields(["instanceIds"]);
  };

  // 获取字典
  const getOptions = async (type: string) => {
    try {
      const arr = ["DOMAIN", "TOPIC", "AI_MODEL_INDUSTRY", "SUPPORT_CHIP", "RULE_ENGINE_INDUSTRY", "SUPPORT_PROTOCOL"];
      let res: any;
      if (arr.includes(type)) {
        res = await api.getNewDict(type);
      } else {
        res = await api.getDict(type);
      }
      if (res?.code == 200) {
        const list = arr.includes(type)
          ? res?.data?.[type].filter((item: any) => item.dictValue !== "ALL")
          : res?.data?.value;
        return list || [];
      } else {
        message.error(res.msg);
        return [];
      }
    } catch (errorInfo: any) {
      message.error(errorInfo?.response?.data?.msg || req_err);
    }
  };

  const domainAndTopic = async () => {
    const { supportChip, kaihongOSVersion, industry } = my_ai_model_detail.dict;
    const topicList: any = await getOptions(supportChip);
    setSupportChipOption(topicList);
    const kaihongOSVersionList: any = await getOptions(kaihongOSVersion);
    setKaihongOSVersionOption([
      ...kaihongOSVersionList,
      {
        dictLabel: "不涉及",
        dictCode: "NO_RELATE",
      },
    ]);
    const industryList: any = await getOptions(industry);
    setIndustryOption(industryList);
  };

  useEffect(() => {
    if (aiModelDetail) {
      const resData = cloneDeep(aiModelDetail);
      const resShowMode = findDeep(release.show_mode_arr, resData?.showMode);
      const nowIndex = resData?.histories.findIndex((item: any) => item.id == resData.id);
      if (nowIndex === 0) {
        //最新版本
        if (resShowMode?.isUpgrade && resData?.histories?.length > 1) {
          // 有升级场景，在新版本页面，按钮要受到旧版本状态影响
          const oldShowMode = resData?.histories[1]?.showMode;
          resData.baseBtns = resShowMode?.btnsByOld[oldShowMode];
        } else {
          resData.baseBtns = resShowMode?.baseBtns;
        }
      } else if (nowIndex === 1) {
        //旧版本
        if (resShowMode?.btnsByNew && resData?.histories?.length > 1) {
          // 有升级场景，在旧版本页面，按钮要受到新版本状态影响
          resData.baseBtns = resShowMode?.btnsByNew[resShowMode];
        } else {
          resData.baseBtns = resShowMode?.baseBtns;
        }
      } else {
        // 历史版本
        resData.baseBtns = resShowMode?.baseBtns;
      }

      resData.showModeLabel = resShowMode?.title;
      resData.isUpgrade = !!resShowMode?.isUpgrade;

      if (resData?.appInstances[0]?.version) {
        resData.showModeLabel = resData?.appInstances[0]?.version + one_bars + resShowMode?.title;
      } else {
        resData.showModeLabel = my_ai_model_detail.new_version + one_bars + resShowMode?.title;
      }
      // 当存在两个版本时候，旧版本都不可修改
      if (resData?.histories?.length > 1 && resData?.histories[1]?.id === id) {
        resData.disabled = true;
      } else {
        resData.disabled = resShowMode?.formDisabled;
      }

      resData.packageDisabled =
        resShowMode?.packageDisabled || (resShowMode?.modeObj && resShowMode?.modeObj[resData.mode]?.packageDisabled);
      resData.allowChangePackageList =
        resShowMode?.allowChangePackageList ||
        (resShowMode?.modeObj && resShowMode?.modeObj[resData.mode]?.allowChangePackageList);

      resData.opinion = resShowMode?.opinion;

      resData.titleDescriptionLabel = resShowMode?.titleDescription
        ? resShowMode?.pleaseWrite
          ? resShowMode?.pleaseWrite + aiModelDetail?.typeKeyInfo?.name + resShowMode?.titleDescription
          : aiModelDetail?.typeKeyInfo?.name + resShowMode?.titleDescription
        : "";
      resData.pakeageType = resData.typeKey !== "AI_MODEL" ? resData.subType.join("/") : "";
      detailData.current = resData;
      initHandle();
    }
  }, [aiModelDetail]);

  useEffect(() => {
    domainAndTopic();
  }, []);

  useEffect(() => {
    baseForm
      .validateFields({ validateOnly: true })
      .then(() => setIsBaseFinish(true))
      .catch(() => setIsBaseFinish(false));
  }, [baseFormValues]);
  useEffect(() => {
    versionForm
      .validateFields({ validateOnly: true })
      .then((values) => {
        console.log("[versionForm] 表单验证通过", values);
        setIsVersionFinish(true);
      })
      .catch((err) => {
        console.log("[versionForm] 表单验证未通过", err?.errorFields ?? err);
        setIsVersionFinish(false);
      });
  }, [versionFormValues]);
  useEffect(() => {
    baseForm.setFieldValue("previewFormData", previewData);
    versionForm.setFieldValue("usageGuideResources", usageGuide);
  }, [previewData]);

  const validateVersion = (_: any, value: any) => {
    if (value && !detailData.current.disabled) {
      return api.existAiModelVersion({ version: value, id }).then((resp: any) => {
        if (resp.code === 200) {
          return Promise.resolve();
        } else {
          return Promise.reject(new Error(resp.msg));
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const validateName = (_: any, value: any) => {
    if (value && !detailData.current.disabled && appNameReg.test(value)) {
      return apiStaging.allRepeatName({ name: value, id }).then((resp: any) => {
        if (resp.code === 200) {
          if (resp.data?.exist) {
            return Promise.reject(new Error(my_ai_model.name_already));
          }
        } else {
          return Promise.resolve();
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const kaihongOSVersionId = Form.useWatch("kaihongOSVersionId", versionForm);

  const isShow = useMemo(() => kaihongOSVersionId !== "NO_RELATE", [kaihongOSVersionId]);

  return (
    <div className={`appDetail-content ${prefix}`}>
      <div className={`${prefix}-header`}>
        <div className={`${prefix}-header-left`}>
          <div title={detailData.current?.showModeLabel} className="one-line-ellipsis">
            {detailData.current?.showModeLabel}
          </div>
          {detailData.current?.titleDescriptionLabel ? <div>{detailData.current?.titleDescriptionLabel}</div> : null}
          <div className="one-line-ellipsis" title={detailData.current?.opinion || detailData.current?.audit?.content}>
            <span>{release.review_comments}</span>
            {detailData.current?.opinion || detailData.current?.audit?.content || two_bars}
          </div>
        </div>
        <div>
          {detailData.current?.baseBtns?.map((item: string) => {
            return <span key={item}>{renderBtn(item)}</span>;
          })}
        </div>
      </div>
      <div className={`${prefix}-content`}>
        <Tabs
          activeKey={activeKey}
          renderTabBar={renderTabBar}
          indicator={{ size: (origin) => origin - 19, align: "start" }}
          onChange={onChange}
          className={`${prefix}-tabs`}
          items={[
            {
              key: release.tabs[0].key,
              forceRender: true,
              label: (
                <>
                  {release.tabs[0].label}
                  {<CheckCircleFilled style={{ color: isBaseFinish ? "#3EC167" : "#8C8C8C", marginLeft: 4 }} />}
                </>
              ),
              children: (
                <Form
                  disabled={detailData.current?.disabled}
                  form={baseForm}
                  layout="vertical"
                  scrollToFirstError={true}
                  className={`${prefix}-baseForm ${prefix}-form`}
                >
                  {typeKey !== "AGENT" && (
                    <Form.Item
                      label={my_ai_model_detail.support_chip_text}
                      name="supportChip"
                      validateDebounce={500}
                      rules={[{ required: true, message: my_ai_model_detail.name_support_chip_placeholder }]}
                    >
                      <Select
                        mode="multiple"
                        options={supportChipOption}
                        placeholder={my_ai_model_detail.name_support_chip_placeholder}
                        fieldNames={{ label: "dictLabel", value: "dictCode" }}
                        filterOption={(input, option) =>
                          (option?.dictLabel ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 524 }}
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    label={release.name_label}
                    name="name"
                    validateDebounce={500}
                    rules={[
                      { required: true, message: my_ai_model_detail.name_placeholder },
                      {
                        pattern: appNameReg,
                        message: my_ai_model_detail.name_validator,
                      },
                      { validator: validateName },
                    ]}
                    getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                  >
                    <Input
                      placeholder={`${my_ai_model_detail.name_placeholder}`}
                      showCount
                      maxLength={30}
                      style={{ width: 524 }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={release.introduction}
                    name="introduction"
                    validateDebounce={500}
                    rules={[{ required: true, message: release.introduction_required }]}
                    getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                  >
                    <Input
                      placeholder={release.introduction_required}
                      showCount
                      maxLength={30}
                      style={{ width: 524 }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={release.description_label}
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: `${please_enter}${description_label}`,
                      },
                    ]}
                    getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                  >
                    <Input.TextArea
                      placeholder={`${please_enter}${description_label}`}
                      showCount
                      maxLength={300}
                      autoSize={{ minRows: 4, maxRows: 6 }}
                      style={{ width: 524 }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={my_ai_model_detail.industry_label}
                    name="industry"
                    rules={[
                      {
                        required: true,
                        message: my_ai_model_detail.industry_label_placeholder,
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      options={industryOption}
                      placeholder={my_ai_model_detail.industry_label_placeholder}
                      fieldNames={{ label: "dictLabel", value: "dictCode" }}
                      filterOption={(input, option) =>
                        (option?.dictLabel ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ width: 524 }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={release.icon_label}
                    name="iconResources"
                    rules={[
                      { required: true, message: `${please_upload}${my_ai_model_detail.detail_title}${icon_text}` },
                    ]}
                    className={`${prefix}-baseForm-iconResources`}
                  >
                    <div>
                      <div className={`${prefix}-form-tip`}>{release.icon_tip}</div>
                      <Upload
                        accept="image/*"
                        listType="picture-card"
                        showUploadList={false}
                        disabled={detailData.current?.disabled || iconLoading}
                        maxCount={1}
                        action={uploadUrl}
                        headers={{
                          Authorization: getAuthTokenValue("Authorization") || "",
                        }}
                        data={{
                          appId: id,
                          type: "ICON",
                          subType: "ICON",
                        }}
                        beforeUpload={async (e) => {
                          await beforeUpload(e, { width: 256, height: 256, size: 3145728, accept: ["png"] });
                        }}
                        onChange={iconImageChange}
                        style={{ margin: 0 }}
                      >
                        {iconImageUrl ? (
                          <img src={iconImageUrl} alt="avatar" />
                        ) : iconLoading ? (
                          <LoadingOutlined />
                        ) : (
                          <PlusSquareFilled className={`${prefix}-add-icon`} />
                        )}
                      </Upload>
                    </div>
                  </Form.Item>
                  <Form.Item
                    name="previewFormData"
                    label={release.preview_screenshot_label}
                    rules={[
                      { required: true, message: release.preview_screenshot_required },
                      ({ getFieldValue }) => ({
                        async validator() {
                          const previewList = getFieldValue("previewFormData");
                          if (previewList.length > 0) {
                            return Promise.resolve();
                          } else {
                            // return Promise.reject(new Error(release.preview_screenshot_validator));
                          }
                        },
                      }),
                    ]}
                  >
                    <div>
                      <div className={`${prefix}-form-tip`}>{release.preview_screenshot_tip}</div>
                      {previewData.map((item, index) => {
                        return (
                          <div key={item.id} className={`${prefix}-upload-img`}>
                            {detailData.current?.disabled ? null : (
                              <CloseCircleFilled
                                className={`${prefix}-upload-img-del`}
                                onClick={() => {
                                  previewImgDelete(index);
                                }}
                              />
                            )}

                            <Upload
                              accept="image/*"
                              listType="picture-card"
                              showUploadList={false}
                              disabled={detailData.current?.disabled || item.loading}
                              maxCount={1}
                              action={uploadUrl}
                              headers={{
                                Authorization: getAuthTokenValue("Authorization") || "",
                              }}
                              data={{
                                appId: id,
                                subType: ["APP", "HAP"],
                                type: "PREVIEW_PICTURE",
                              }}
                              beforeUpload={async (e) => {
                                await beforeUpload(e, {
                                  width: 1800,
                                  height: 920,
                                  size: 3145728,
                                  accept: ["jpg", "jpeg", "png"],
                                });
                              }}
                              onChange={(e) => {
                                previewImgUpdate(e, index);
                              }}
                            >
                              {item.loading ? <LoadingOutlined /> : <img src={item?.url} alt="avatar" />}
                            </Upload>
                          </div>
                        );
                      })}
                      {previewData?.length >= 10 ? null : (
                        <Upload
                          accept="image/*"
                          listType="picture-card"
                          className={`${prefix}-upload-imgAdd`}
                          disabled={detailData.current?.disabled || previewLoading}
                          action={uploadUrl}
                          headers={{
                            Authorization: getAuthTokenValue("Authorization") || "",
                          }}
                          data={{
                            appId: id,
                            subType: ["APP", "HAP"],
                            type: "PREVIEW_PICTURE",
                          }}
                          showUploadList={false}
                          beforeUpload={async (e) => {
                            await beforeUpload(e, {
                              width: 1800,
                              height: 920,
                              size: 3145728,
                              accept: ["jpg", "jpeg", "png"],
                            });
                          }}
                          onChange={(e) => {
                            previewImgChange(e);
                          }}
                        >
                          {previewLoading ? <LoadingOutlined /> : <PlusSquareFilled className={`${prefix}-add-icon`} />}
                        </Upload>
                      )}
                    </div>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: release.tabs[1].key,
              forceRender: true,
              label: (
                <>
                  {release.tabs[1].label}
                  {<CheckCircleFilled style={{ color: isVersionFinish ? "#3EC167" : "#8C8C8C", marginLeft: 4 }} />}
                </>
              ),
              children: (
                <Form
                  form={versionForm}
                  disabled={detailData.current?.disabled}
                  layout="vertical"
                  scrollToFirstError={true}
                  className={`${prefix}-versionForm ${prefix}-form`}
                >
                  <Form.Item
                    label={release.package_label}
                    name="instanceIds"
                    rules={[{ required: true, message: release.package_required }]}
                    className={`${prefix}-versionForm-packageResources`}
                  >
                    <div>
                      <div className={`${prefix}-form-tip`}>
                        {detailData.current?.pakeageType
                          ? my_ai_model_detail.packageTipPrefix +
                            detailData.current?.pakeageType +
                            my_ai_model_detail.packageTipSuffix
                          : my_ai_model_detail.packageTip}
                      </div>
                      {packageData?.resourceId ? (
                        <div className={`${prefix}-package-data`} style={{ width: 524 }}>
                          <span title={packageData?.name} className="one-line-ellipsis">
                            {packageData?.name}
                          </span>
                          <span title={packageData?.version} className="one-line-ellipsis">
                            {packageData?.version}
                          </span>
                          <span>{format(new Date(Number(packageData?.createTime)), "yyyy/MM/dd-HH:mm:ss")}</span>
                          <span>{changeSize(packageData?.length)}</span>
                          {detailData.current?.disabled || detailData.current?.packageDisabled ? null : (
                            <CloseCircleFilled
                              style={{ color: "#000", cursor: "pointer" }}
                              className={`${prefix}-package-del`}
                              onClick={packageDel}
                            />
                          )}
                        </div>
                      ) : packageLoading ? (
                        <div className={`${prefix}-package-loading`} style={{ width: 524 }}>
                          <span title={packageData?.name} className="one-line-ellipsis">
                            {packageData?.name}
                          </span>
                          {uploadProgress < 100 ? (
                            <span>
                              {release.uploading_label}
                              <Progress type="circle" size={30} percent={uploadProgress} />
                            </span>
                          ) : (
                            <span>
                              {release.analysising_label}
                              <LoadingOutlined style={{ color: "#0A59F7", fontSize: 30 }} />
                            </span>
                          )}
                        </div>
                      ) : (
                        <Upload
                          beforeUpload={packageBeforeUpload}
                          customRequest={packageReq}
                          showUploadList={false}
                          style={{ margin: 0 }}
                        >
                          <div className={`${prefix}-package-add`}>
                            <CloudUploadOutlined className={`${prefix}-package-cloud`} />
                            <PlusSquareFilled className={`${prefix}-package-plus`} />
                          </div>
                        </Upload>
                      )}
                    </div>
                  </Form.Item>
                  <Divider />
                  <Form.Item
                    label={my_ai_model_detail.package_version}
                    name="version"
                    validateDebounce={500}
                    rules={[
                      { required: true, message: my_ai_model_detail.package_version_placeholder },
                      // {
                      //   pattern: devicePluginVersionNameReg,
                      //   message: my_ai_model_detail.version_validator,
                      // },
                      {
                        validator: validateVersion,
                      },
                    ]}
                    getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                  >
                    <Input
                      placeholder={`${my_ai_model_detail.package_version_placeholder}`}
                      style={{ width: 524 }}
                      showCount
                      maxLength={30}
                    />
                  </Form.Item>
                  <Divider />
                  <Form.Item
                    label={
                      <span>
                        <span className={`${prefix}-required-extra`}>*</span>
                        {release.systemVersion_label}
                      </span>
                    }
                  >
                    <Form.Item
                      name="kaihongOSVersionId"
                      rules={[
                        {
                          required: true,
                          message: release.systemVersion_required,
                        },
                      ]}
                      style={{ display: "inline-block", marginBottom: 0 }}
                    >
                      <Select
                        showSearch
                        options={kaihongOSVersionOption}
                        placeholder={release.systemVersion_required}
                        fieldNames={{ label: "dictLabel", value: "dictCode" }}
                        filterOption={(input, option) =>
                          (option?.dictLabel ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                      />
                    </Form.Item>

                    {isShow && (
                      <Form.Item
                        name="kaihongOSVersionType"
                        rules={[{ required: true, message: release.kaihongOSVersion_type_required }]}
                        style={{ display: "inline-block", marginLeft: 24, marginBottom: 0 }}
                      >
                        <Select
                          options={release.supportType_list}
                          placeholder={release.kaihongOSVersion_type_required}
                          style={{ width: 200 }}
                        />
                      </Form.Item>
                    )}
                  </Form.Item>

                  <Divider />

                  <Form.Item
                    label={my_ai_model_detail.usage_guide_text}
                    name="usageGuideResources"
                    className={`${prefix}-versionForm-packageResources`}
                  >
                    <div>
                      <div className={`${prefix}-form-tip`}>{my_ai_model_detail.usage_guide_tip}</div>
                      {usageGuide?.resourceId ? (
                        <div className={`${prefix}-package-data`} style={{ width: 524 }}>
                          <span title={usageGuide?.name} className="one-line-ellipsis">
                            {usageGuide?.name}
                          </span>
                          <span title={usageGuide?.version} className="one-line-ellipsis">
                            {usageGuide?.version}
                          </span>
                          <span>{format(new Date(Number(usageGuide?.createTime)), "yyyy/MM/dd-HH:mm:ss")}</span>
                          <span>{changeSize(usageGuide?.length)}</span>
                          {detailData.current?.disabled || detailData.current?.packageDisabled ? null : (
                            <CloseCircleFilled
                              style={{ color: "#000", cursor: "pointer" }}
                              className={`${prefix}-package-del`}
                              onClick={pdfDel}
                            />
                          )}
                        </div>
                      ) : usageGuideLoading ? (
                        <div className={`${prefix}-package-loading`} style={{ width: 524 }}>
                          <span title={usageGuide?.name} className="one-line-ellipsis">
                            {usageGuide?.name}
                          </span>
                          {uploadProgressPdf < 100 ? (
                            <span>
                              {release.uploading_label}
                              <Progress type="circle" size={30} percent={uploadProgressPdf} />
                            </span>
                          ) : (
                            <span>
                              {release.analysising_label}
                              <LoadingOutlined style={{ color: "#0A59F7", fontSize: 30 }} />
                            </span>
                          )}
                        </div>
                      ) : (
                        <Upload
                          beforeUpload={pdfBeforeUpload}
                          customRequest={pdfReq}
                          showUploadList={false}
                          style={{ margin: 0 }}
                        >
                          <div className={`${prefix}-package-add`}>
                            <PlusSquareFilled className={`${prefix}-add-icon ${prefix}-package-position`} />
                          </div>
                        </Upload>
                      )}
                    </div>
                  </Form.Item>
                  <Divider />
                  <Form.Item
                    label={release.privacy_label}
                    name="privacy"
                    rules={[
                      {
                        pattern: /(http|https):\/\/([\w.]+\/?)\S*/,
                        message: release.privacy_validator,
                      },
                    ]}
                    getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                  >
                    <Input placeholder={release.privacy_placeholder} maxLength={1024} style={{ width: 524 }} />
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
      </div>
      <ApplyDown
        type={typeKey}
        id={id}
        visible={applyDownVisible}
        close={applyDownClose}
        title={aiModelDetail?.typeKeyInfo?.name}
      />
    </div>
  );
};

export default VersionManage;
